import { tutorialTaskDone } from '../../tutorial/events'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { athleteMeta, legacySportKeys, sportValue } from '../data/athletes'
import { useAthlete, useClubContext, useGroups, useInvalidateAthlete, useSingleFlight, useSportFields } from '../data/hooks'
import { SliceApiError, type Athlete, type AthletePatch, type SportTypeField } from '../data/types'
import { LimitNote, limitReason } from '../components/LimitNote'
import { useBackTo } from './AthleteScreen'
import { liveGroupId } from '../../utils/liveGroup'

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function cut(v: string | undefined | null): string {
  return v ? v.slice(0, 10) : ''
}

/** Сравнение «было - стало» по строкам: в запрос уходят только изменения. */
function changed(before: string, after: string): boolean {
  return before.trim() !== after.trim()
}

// ---------------------------------------------------------------------------
// Правка карточки (S08 «Редактировать», SC05)
// ---------------------------------------------------------------------------

interface EditForm {
  name: string
  dateOfBirth: string
  groupId: string
  parentPhone: string
  phone: string
  weight: string
  height: string
  clearance: string
  notes: string
}

function toForm(a: Athlete): EditForm {
  return {
    name: a.name,
    dateOfBirth: cut(a.date_of_birth),
    groupId: a.group_id ?? '',
    parentPhone: a.parent_phone ?? '',
    phone: a.phone ?? '',
    weight: a.weight ? String(a.weight) : '',
    height: a.height ? String(a.height) : '',
    clearance: cut(a.medical_clearance),
    notes: a.notes ?? '',
  }
}

export function AthleteEditScreen() {
  const { id = '' } = useParams()
  const athlete = useAthlete(id)
  const back = useBackTo(`/athletes/${id}`)

  if (athlete.isPending) {
    return (
      <Screen title="Редактирование" back={back}>
        <Skeleton rows={4} />
      </Screen>
    )
  }
  if (athlete.isError) {
    return (
      <Screen title="Редактирование" back={back}>
        <ErrorState title="Не удалось открыть карточку" onRetry={() => void athlete.refetch()} />
      </Screen>
    )
  }
  return <EditFormView athlete={athlete.data} />
}

function EditFormView({ athlete: a }: { athlete: Athlete }) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const groups = useGroups()
  const invalidate = useInvalidateAthlete()
  const initial = useMemo(() => toForm(a), [a])
  const [form, setForm] = useState<EditForm>(initial)
  const [errors, setErrors] = useState<Partial<Record<keyof EditForm, string>>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)

  const dirty = (Object.keys(form) as (keyof EditForm)[]).some((k) => changed(initial[k], form[k]))
  const leave = useLeaveGuard(dirty)
  const back = () => leave.guard(() => navigate(`/athletes/${a.id}`, { replace: true }))

  const set = (k: keyof EditForm) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }))

  // Пустая строка на сервере значение не стирает: предупреждаем прямо у поля.
  const clearedHint = (k: keyof EditForm) =>
    initial[k] && !form[k].trim() ? 'Стереть значение пока нельзя: при сохранении останется прежнее.' : undefined

  const validate = (): boolean => {
    const e: Partial<Record<keyof EditForm, string>> = {}
    if (!form.name.trim()) e.name = 'Укажите имя'
    for (const k of ['dateOfBirth', 'clearance'] as const) {
      if (form[k] && !DATE_RE.test(form[k])) e[k] = 'Дата в формате ГГГГ-ММ-ДД'
    }
    if (form.weight && !(Number(form.weight) > 0 && Number(form.weight) < 300)) e.weight = 'Вес в килограммах, например 32.5'
    if (form.height && !(Number.isInteger(Number(form.height)) && Number(form.height) > 30 && Number(form.height) < 250)) {
      e.height = 'Рост в сантиметрах целым числом, например 128'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const save = async () => {
    if (!validate()) return
    const patch: AthletePatch = {}
    if (changed(initial.name, form.name)) patch.name = form.name.trim()
    if (changed(initial.dateOfBirth, form.dateOfBirth) && form.dateOfBirth) patch.date_of_birth = form.dateOfBirth
    if (form.groupId !== initial.groupId) patch.group_id = form.groupId
    if (changed(initial.parentPhone, form.parentPhone)) patch.parent_phone = form.parentPhone.trim()
    if (changed(initial.phone, form.phone)) patch.phone = form.phone.trim()
    if (changed(initial.weight, form.weight) && form.weight) patch.weight = Number(form.weight)
    if (changed(initial.height, form.height) && form.height) patch.height = Number(form.height)
    if (changed(initial.clearance, form.clearance) && form.clearance) patch.medical_clearance = form.clearance
    if (changed(initial.notes, form.notes)) patch.notes = form.notes.trim()

    setSaving(true)
    setSaveError(false)
    try {
      await api.updateAthlete(a.id, patch)
      invalidate(a.id)
      toast('Изменения сохранены')
      navigate(`/athletes/${a.id}`, { replace: true })
    } catch {
      setSaveError(true)
      setSaving(false)
    }
  }

  return (
    <Screen
      title="Редактирование"
      subtitle={a.name}
      back={back}
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--primary"
            style={{ flex: 2 }}
            disabled={saving || !dirty}
            onClick={() => void save()}
          >
            {saving ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <form
        className="pc-form"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
        noValidate
      >
        {saveError && (
          <SaveFailedAlert>Изменения остались на экране, попробуйте ещё раз.</SaveFailedAlert>
        )}
        <Field label="Имя и фамилия" error={errors.name}>
          {(p) => <input {...p} className="pc-input" value={form.name} onChange={set('name')} autoComplete="off" />}
        </Field>
        <Field label="Дата рождения" error={errors.dateOfBirth} hint={clearedHint('dateOfBirth')}>
          {(p) => <input {...p} className="pc-input" type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} />}
        </Field>
        <Field label="Группа">
          {(p) => (
            // Удалённой группы в списке нет: показываем «Без группы». Пока
            // поле не тронули, на сервер ничего не уходит, ссылка в данных
            // остаётся.
            <select {...p} className="pc-input" value={liveGroupId(form.groupId, groups.data) ?? ''} onChange={set('groupId')}>
              <option value="">Без группы</option>
              {(groups.data ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Телефон родителя" hint={clearedHint('parentPhone')}>
          {(p) => <input {...p} className="pc-input" type="tel" value={form.parentPhone} onChange={set('parentPhone')} />}
        </Field>
        <Field label="Телефон спортсмена" hint={clearedHint('phone')}>
          {(p) => <input {...p} className="pc-input" type="tel" value={form.phone} onChange={set('phone')} />}
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Рост, см" error={errors.height} hint={clearedHint('height')}>
            {(p) => <input {...p} className="pc-input" inputMode="numeric" value={form.height} onChange={set('height')} />}
          </Field>
          <Field label="Вес, кг" error={errors.weight} hint={clearedHint('weight')}>
            {(p) => <input {...p} className="pc-input" inputMode="decimal" value={form.weight} onChange={set('weight')} />}
          </Field>
        </div>
        <Field label="Медицинский допуск до" error={errors.clearance} hint={clearedHint('clearance')}>
          {(p) => <input {...p} className="pc-input" type="date" value={form.clearance} onChange={set('clearance')} />}
        </Field>
        <Field label="Заметки" hint={clearedHint('notes')}>
          {(p) => <textarea {...p} className="pc-input" value={form.notes} onChange={set('notes')} />}
        </Field>
      </form>
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Спортивный профиль (S09, SC05)
// ---------------------------------------------------------------------------

export function AthleteSportScreen() {
  const { id = '' } = useParams()
  const athlete = useAthlete(id)
  const club = useClubContext()
  const fields = useSportFields(club.data?.sportName)
  const back = useBackTo(`/athletes/${id}`)

  if (athlete.isPending || fields.isPending) {
    return (
      <Screen title="Спортивные данные" back={back}>
        <Skeleton rows={3} />
      </Screen>
    )
  }
  if (athlete.isError || fields.isError) {
    return (
      <Screen title="Спортивные данные" back={back}>
        <ErrorState
          title="Не удалось загрузить спортивные данные"
          onRetry={() => {
            void athlete.refetch()
            void fields.refetch()
          }}
        />
      </Screen>
    )
  }
  return <SportForm athlete={athlete.data} fields={fields.data} sportName={club.data?.sportName ?? ''} directionName={club.data?.directionName ?? ''} />
}

function SportForm({
  athlete: a,
  fields,
  sportName,
  directionName,
}: {
  athlete: Athlete
  fields: SportTypeField[]
  sportName: string
  directionName: string
}) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const invalidate = useInvalidateAthlete()
  const initial = useMemo(() => Object.fromEntries(fields.map((f) => [f.field_key, sportValue(a, f.field_key) ?? ''])), [a, fields])
  const [values, setValues] = useState<Record<string, string>>(initial)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const legacy = legacySportKeys(a, fields)

  const dirty = fields.some((f) => (values[f.field_key] ?? '') !== (initial[f.field_key] ?? ''))
  const leave = useLeaveGuard(dirty)
  const back = () => leave.guard(() => navigate(`/athletes/${a.id}`, { replace: true }))

  const save = async () => {
    const e: Record<string, string> = {}
    const patch: Record<string, string | number> = {}
    for (const f of fields) {
      const v = (values[f.field_key] ?? '').trim()
      if (v === (initial[f.field_key] ?? '')) continue
      if (f.field_type === 'number' && v && Number.isNaN(Number(v))) {
        e[f.field_key] = 'Нужно число'
        continue
      }
      if (f.field_type === 'date' && v && !DATE_RE.test(v)) {
        e[f.field_key] = 'Дата в формате ГГГГ-ММ-ДД'
        continue
      }
      // Поле шаблона можно и очистить: sport_data хранит пустую строку.
      patch[f.field_key] = f.field_type === 'number' && v ? Number(v) : v
    }
    setErrors(e)
    if (Object.keys(e).length > 0) return

    setSaving(true)
    setSaveError(false)
    try {
      await api.updateAthlete(a.id, { sport_data: patch })
      invalidate(a.id)
      toast('Спортивные данные сохранены')
      navigate(`/athletes/${a.id}`, { replace: true })
    } catch {
      setSaveError(true)
      setSaving(false)
    }
  }

  return (
    <Screen
      title="Спортивные данные"
      subtitle={`${a.name} / ${directionName}`}
      back={back}
      actionBar={
        fields.length > 0 ? (
          <>
            <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
              Отмена
            </button>
            <button
              type="button"
              className="pc-btn pc-btn--primary"
              style={{ flex: 2 }}
              disabled={saving || !dirty}
              onClick={() => void save()}
            >
              {saving ? 'Сохраняю…' : 'Сохранить'}
            </button>
          </>
        ) : undefined
      }
    >
      {fields.length === 0 ? (
        <EmptyState
          title="Для этого вида спорта нет спортивных полей"
          text={sportName ? `Шаблон полей для «${sportName}» пока не настроен.` : 'У направления не указан вид спорта.'}
        />
      ) : (
        <form
          className="pc-form"
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
          noValidate
        >
          {saveError && (
            <SaveFailedAlert>Значения остались на экране, попробуйте ещё раз.</SaveFailedAlert>
          )}
          {fields.map((f) => (
            <Field key={f.field_key} label={f.field_label} error={errors[f.field_key]}>
              {(p) => {
                const value = values[f.field_key] ?? ''
                const onChange = (e: { target: { value: string } }) => setValues((v) => ({ ...v, [f.field_key]: e.target.value }))
                if (f.field_type === 'select') {
                  const options = f.field_options ?? []
                  return (
                    <select {...p} className="pc-input" value={value} onChange={onChange}>
                      <option value="">Не указано</option>
                      {/* Старое значение вне списка не теряем: оно остаётся выбранным. */}
                      {value && !options.includes(value) && <option value={value}>{value}</option>}
                      {options.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  )
                }
                return (
                  <input
                    {...p}
                    className="pc-input"
                    type={f.field_type === 'date' ? 'date' : 'text'}
                    inputMode={f.field_type === 'number' ? 'decimal' : undefined}
                    value={value}
                    onChange={onChange}
                  />
                )
              }}
            </Field>
          ))}

          <div className="pc-note">
            Эти поля заданы шаблоном вида спорта «{sportName}». Изменение шаблона не удаляет уже записанные значения.
          </div>

          {legacy.length > 0 && (
            <section aria-labelledby="pc-legacy">
              <h2 id="pc-legacy" className="pc-section-title">
                Поля из прежней версии
              </h2>
              <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
                Их нет в шаблоне, но значения сохранены и не пропадут.
              </p>
              <ul className="pc-list">
                {legacy.map(({ key, value }) => (
                  <li key={key}>
                    <div className="pc-kv">
                      <div className="pc-kv__label">{key}</div>
                      <div className="pc-kv__value">{value}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </form>
      )}
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Новый спортсмен (SC04)
// ---------------------------------------------------------------------------

export function AthleteNewScreen() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { api, now, toast } = useEnv()
  const groups = useGroups()
  const invalidate = useInvalidateAthlete()
  const flight = useSingleFlight()
  const [name, setName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [groupId, setGroupId] = useState(params.get('g') ?? '')
  const [parentPhone, setParentPhone] = useState('')
  const [errors, setErrors] = useState<{ name?: string; dateOfBirth?: string }>({})
  // check - проверка на дубль не ответила, ничего не создано
  const [failure, setFailure] = useState<null | 'limit' | 'locked' | 'check' | 'failed'>(null)
  const [dupes, setDupes] = useState<Athlete[] | null>(null)

  // Отказ виден, даже если кнопку нажали внизу формы.
  const failureRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (failure) failureRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [failure])

  const dirty = !!(name.trim() || dateOfBirth || parentPhone.trim())
  const leave = useLeaveGuard(dirty)
  const back = () => leave.guard(() => navigate(-1))
  const groupName = useMemo(() => new Map((groups.data ?? []).map((g) => [g.id, g.name])), [groups.data])

  useEffect(() => setDupes(null), [name])

  /**
   * Проверка на дубль - на сервере (уменьшительные, порядок слов), затем
   * создание. Всё - один полёт: второе нажатие, пока идёт проверка или
   * создание, ничего не шлёт.
   */
  const create = async (skipDupes: boolean) => {
    const e: typeof errors = {}
    if (!name.trim()) e.name = 'Укажите имя и фамилию'
    if (dateOfBirth && !DATE_RE.test(dateOfBirth)) e.dateOfBirth = 'Дата в формате ГГГГ-ММ-ДД'
    setErrors(e)
    if (Object.keys(e).length > 0) return

    setFailure(null)
    await flight.run(async () => {
      if (!skipDupes) {
        let found: Athlete[]
        try {
          found = await api.findSimilarAthletes(name.trim())
        } catch (err) {
          // Без сети не создать и не проверить - обычный «не сохранилось».
          setFailure(err instanceof SliceApiError && err.kind === 'network' ? 'failed' : 'check')
          return
        }
        if (found.length > 0) {
          setDupes(found)
          return
        }
      }
      try {
        const created = await api.createAthlete({ name: name.trim(), dateOfBirth, groupId, parentPhone: parentPhone.trim() })
        tutorialTaskDone('athlete')
        invalidate(created.id)
        toast('Спортсмен добавлен')
        navigate(`/athletes/${created.id}`, { replace: true })
      } catch (err) {
        setFailure(limitReason(err))
      }
    })
  }

  return (
    <Screen
      title="Новый спортсмен"
      back={back}
      actionBar={
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={flight.busy}
          onClick={() => void create(false)}
        >
          {flight.busy ? 'Сохраняю…' : 'Добавить'}
        </button>
      }
    >
      <form
        className="pc-form"
        onSubmit={(e) => {
          e.preventDefault()
          void create(false)
        }}
        noValidate
      >
        <div ref={failureRef}>
          {failure === 'failed' && <SaveFailedAlert>Данные остались на экране, попробуйте ещё раз.</SaveFailedAlert>}
          {(failure === 'limit' || failure === 'locked') && (
            <LimitNote what="спортсменов" reason={failure} hint="Спортсмены на паузе места в лимите не занимают." />
          )}
          {failure === 'check' && (
            <div className="pc-note" role="alert">
              Не удалось проверить, нет ли такого спортсмена уже. Ничего не создано: можно повторить или добавить без проверки.
              <button
                type="button"
                className="pc-btn pc-btn--block"
                style={{ marginTop: 8 }}
                disabled={flight.busy}
                onClick={() => void create(true)}
              >
                Добавить без проверки
              </button>
            </div>
          )}
        </div>
        <Field label="Имя и фамилия" error={errors.name}>
          {(p) => (
            <input {...p} className="pc-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          )}
        </Field>
        <Field label="Группа" hint="Можно назначить позже">
          {(p) => (
            <select {...p} className="pc-input" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Без группы</option>
              {(groups.data ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Дата рождения" error={errors.dateOfBirth} hint="Возраст считается из неё">
          {(p) => <input {...p} className="pc-input" type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />}
        </Field>
        <Field label="Телефон родителя">
          {(p) => <input {...p} className="pc-input" type="tel" value={parentPhone} onChange={(e) => setParentPhone(e.target.value)} />}
        </Field>
        <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
          Спортивные поля подтянутся из направления, остальное можно заполнить в карточке позже.
        </p>
      </form>

      {dupes && (
        <Sheet title="Похоже, такой спортсмен уже есть" onClose={() => setDupes(null)}>
          <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
            Проверьте, не тот же ли это человек. Записи не объединяются сами.
          </p>
          <ul className="pc-list" style={{ marginBottom: 12 }}>
            {dupes.map((d) => (
              <li key={d.id}>
                <button type="button" className="pc-list-row" onClick={() => navigate(`/athletes/${d.id}`)}>
                  <span className="pc-list-row__main">
                    <span className="pc-list-row__title" style={{ display: 'block' }}>
                      {d.name}
                    </span>
                    <span className="pc-list-row__meta">
                      {athleteMeta(d, d.group_id ? groupName.get(d.group_id) : undefined, now())}
                    </span>
                  </span>
                  <span className="pc-list-row__aside">Открыть</span>
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--primary"
            disabled={flight.busy}
            onClick={() => {
              setDupes(null)
              void create(true)
            }}
          >
            Это другой человек, добавить
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setDupes(null)}>
            Отмена
          </button>
        </Sheet>
      )}
      {leave.dialog}
    </Screen>
  )
}
